import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { ActionPanels } from '../components/ActionPanels';
import { Tradesperson } from '@/lib/types';
import { debug } from '@/lib/utils';
import { TradeListContainer } from '../components/TradeListContainer';
import { ActionButtonType } from '../components/ActionButtons';
import { TradesPurpleHeader } from '../components/TradesPurpleHeader';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { User as UserIcon } from 'lucide-react';
import TradesPurpleLayout from '../components/Layout';
import { TabId, TabBarActionButton } from '../components/ui/tab-bar';

export default function LoggedInLandingPage() {
  const [activeButton, setActiveButton] = useState<ActionButtonType | null>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [tradespeopleCount, setTradespeopleCount] = useState(0);
  const [filteredTradespeople, setFilteredTradespeople] = useState<Tradesperson[]>([]);
  const [profileComplete, setProfileComplete] = useState<boolean | null>(null);
  const [userPostcode, setUserPostcode] = useState<string | null>(null);
  const [availableTradesCount, setAvailableTradesCount] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<TabId>('list');
  const [actionButton, setActionButton] = useState<TabBarActionButton | undefined>(undefined);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Profile completeness comes straight from the authenticated user, which is
  // already loaded with username/postcode via /auth/session. The previous code
  // queried Supabase directly (`supabase.from('profiles')`) — dead since the D1
  // migration and an undefined reference at runtime, so it always threw and set
  // profileComplete=false, showing every logged-in user "Complete Your Profile".
  useEffect(() => {
    if (!user) return;
    setProfileComplete(!!(user.username && user.postcode));
    if (user.postcode) setUserPostcode(user.postcode);
  }, [user]);

  // Check available trades in user's postcode
  useEffect(() => {
    async function checkAvailableTrades() {
      // Only check if we have a postcode and less than 5 saved trades
      if (!userPostcode || tradespeopleCount >= 5) return;

      try {
        // Import the function to get trades by postcode
        const { getTradesPeopleByPostcodeWithCounts } = await import('@/lib/supabase/trades');

        const availableTrades = await getTradesPeopleByPostcodeWithCounts(userPostcode, 50);
        setAvailableTradesCount(availableTrades.length);
      } catch (error) {
        debug.error('Error checking available trades:', error);
        setAvailableTradesCount(0);
      }
    }

    checkAvailableTrades();
  }, [userPostcode, tradespeopleCount]);

  // Define handleButtonClick before useEffect to avoid temporal dead zone
  const handleButtonClick = (button: ActionButtonType) => {
    // If clicking the same button, toggle it off
    if (activeButton === button) {
      setActiveButton(null);
    } else {
      setActiveButton(button);
    }
  };

  // Set up custom event listener for the prominent Add Trade button
  useEffect(() => {
    const handleTradeAction = (event: CustomEvent<{action: ActionButtonType}>) => {
      if (event.detail && event.detail.action) {
        // If clicking the same button, toggle it off
        if (activeButton === event.detail.action) {
          setActiveButton(null);
        } else {
          setActiveButton(event.detail.action);
        }
      }
    };

    // Use EventListener instead of any
    window.addEventListener('tradespurple-action', handleTradeAction as EventListener);

    return () => {
      window.removeEventListener('tradespurple-action', handleTradeAction as EventListener);
    };
  }, [activeButton]);

  // Handle category filter change
  const handleCategoryChange = (category: string | null) => {
    setActiveFilter(category);
  };

  // Callback for when a tradesperson is added or removed
  const handleTradespersonDataChange = (trade: Tradesperson) => {
    debug.log('Tradesperson data changed, refreshing TradeList');
    // Increment the trigger to force the TradeList to refresh its data and animate new items
    setRefreshTrigger(prev => prev + 1);
  };

  // Callback when trade list data is loaded
  // Use useCallback to create a stable function reference that doesn't change on every render
  const handleTradesLoaded = useCallback((trades: Tradesperson[]) => {
    setTradespeopleCount(trades.length);

    // Only update filteredTradespeople if the data actually changed
    // This prevents unnecessary re-renders and breaks the infinite loop
    if (trades.length === 0 && filteredTradespeople.length === 0) {
      // Don't update state if both are empty arrays
      return;
    }

    // Simple check to avoid unnecessary updates
    // Note: a deep equality check would be more accurate but also more expensive
    const tradesChanged = JSON.stringify(trades.map(t => t.id || t)) !==
                          JSON.stringify(filteredTradespeople.map(t => t.id || t));

    if (tradesChanged) {
      debug.log('LoggedInLandingPage: Filtered tradespeople updated', {
        count: trades.length,
        changed: true
      });
      setFilteredTradespeople(trades);
    } else {
      debug.log('LoggedInLandingPage: Skipping update (data unchanged)', {
        count: trades.length,
        changed: false
      });
    }
  }, [filteredTradespeople]);

  // After closing a panel, force refresh the list if we were quoting
  const handlePanelClose = () => {
    if (activeButton === 'quote') {
      // Trigger a refresh when closing quote panel since it might modify data
      setRefreshTrigger(prev => prev + 1);
    }
    setActiveButton(null);
    setActiveTab('list'); // Return to list tab when closing panels
  };

  // Handle tab changes
  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);

    // Map tab selection to button state
    switch (tab) {
      case 'add':
        setActiveButton(activeButton === 'add' ? null : 'add');
        break;
      case 'smartquote':
        setActiveButton('quote');
        break;
      case 'list':
      default:
        setActiveButton(null);
        break;
    }
  };

  // Handle action button state from SmartQuote
  const handleActionButtonChange = useCallback((show: boolean) => {
    if (show && activeButton === 'quote') {
      setActionButton({
        label: 'All Done!',
        icon: 'check',
        variant: 'success',
        onClick: () => {
          // Close the quote panel and return to list
          setActiveButton(null);
          setActiveTab('list');
          setActionButton(undefined);
        }
      });
    } else {
      setActionButton(undefined);
    }
  }, [activeButton]);

  // If profile is incomplete, show the profile completion prompt
  if (profileComplete === false) {
    return (
      <TradesPurpleLayout>
        <Helmet>
          <title>TradesPurple | Complete Your Profile</title>
          <meta name="description" content="Complete your profile to start managing and sharing trusted tradespeople." />
          <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
          <meta name="theme-color" content="#7e22ce" />
        </Helmet>

        <div className="flex flex-col pb-8 pb-safe safe-inset-bottom">
          {/* Sticky header */}
          <TradesPurpleHeader forceCompact={true} />

          {/* Profile completion prompt */}
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center max-w-md">
              <div className="mb-6 mx-auto w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center">
                <UserIcon className="h-10 w-10 text-purple-700" />
              </div>
              <h2 className="text-2xl font-bold text-purple-900 mb-3">Complete Your Profile</h2>
              <p className="text-lg text-purple-700 mb-6">Choose a username and enter your postcode to start building your trusted tradespeople list.</p>
              <Button
                onClick={() => {
                  navigate('/auth/onboarding');
                }}
                className="bg-purple-700 hover:bg-purple-800 text-white font-medium py-3 px-6 rounded-lg text-lg shadow-md"
              >
                Complete Profile
              </Button>
            </div>
          </div>
        </div>
      </TradesPurpleLayout>
    );
  }

  return (
    <TradesPurpleLayout
      activeTab={activeTab}
      onTabChange={handleTabChange}
      showTabBar={true}
      actionButton={actionButton}
    >
      <Helmet>
        <title>TradesPurple | Your Trusted Trades List</title>
        <meta name="description" content="Manage and share your trusted tradespeople with your neighbours." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
        <meta name="theme-color" content="#7e22ce" />
      </Helmet>

      <div className="flex flex-col pb-8 pb-safe safe-inset-bottom">
        {/* Sticky header */}
        <TradesPurpleHeader forceCompact={true} />

        {/* Active Panel */}
        <ActionPanels
          activePanel={activeButton}
          onClose={handlePanelClose}
          onDataChange={handleTradespersonDataChange}
          filteredTradespeople={filteredTradespeople}
          onActionButtonChange={handleActionButtonChange}
        />

        {/* Trade List Container - Hidden when SmartQuote is active */}
        {activeButton !== 'quote' && (
          <>
            <TradeListContainer
              mode="personal"
              dataSource="user"
              categoryFilter={activeFilter}
              onCategoryChange={handleCategoryChange}
              onTradespersonSaved={handleTradespersonDataChange}
              onDataLoaded={handleTradesLoaded}
              showHeader={false}
              showActionButtons={false}
              refreshTrigger={refreshTrigger}
            />

            {/* Show "Find more" link if user has < 5 trades and more are available */}
            {tradespeopleCount < 5 && availableTradesCount > tradespeopleCount && userPostcode && (
              <div className="px-4 py-4">
                <div className="text-center py-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">Looking for more trusted tradespeople?</p>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/${userPostcode}`)}
                    className="text-purple-700 border-purple-300 hover:bg-purple-50"
                  >
                    Find more in {userPostcode}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </TradesPurpleLayout>
  );
}
